import React, { useEffect, useMemo, useState } from 'react';
import './AddressCard.css';
import {
    getPhilippinesBarangays,
    getPhilippinesCities,
    getPhilippinesProvinces,
    getPhilippinesRegions,
    normalizePhilippinesAddress,
} from '../../utils/metroManilaLocations';

/**
 * AddressCard component - Individual address card with edit/delete functionality
 * Displays a single address with options to edit, delete, and set as default
 */
function AddressCard({ 
    address, 
    isEditing, 
    editingData, 
    onEdit, 
    onSave, 
    onCancel, 
    onDelete, 
    onSetDefault,
    onInputChange, 
    showDelete,
    saving
}) {
    const data = isEditing ? editingData : address;
    const [regionOptions, setRegionOptions] = useState([]);
    const [provinceOptions, setProvinceOptions] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);
    const [barangayOptions, setBarangayOptions] = useState([]);

    useEffect(() => {
        let isMounted = true;
        getPhilippinesRegions().then((regions) => {
            if (isMounted) setRegionOptions(regions);
        }).catch((error) => {
            console.error('Failed to load Philippine regions:', error);
        });
        return () => { isMounted = false; };
    }, []);

    useEffect(() => {
        let isMounted = true;
        if (!data.region) {
            setProvinceOptions([]);
            return undefined;
        }
        getPhilippinesProvinces(data.region).then((provinces) => {
            if (isMounted) setProvinceOptions(provinces);
        }).catch((error) => {
            console.error('Failed to load Philippine provinces:', error);
        });
        return () => { isMounted = false; };
    }, [data.region]);

    useEffect(() => {
        let isMounted = true;
        if (!data.region || !data.province) {
            setCityOptions([]);
            return undefined;
        }
        getPhilippinesCities(data.region, data.province).then((cities) => {
            if (isMounted) setCityOptions(cities);
        }).catch((error) => {
            console.error('Failed to load Philippine cities:', error);
        });
        return () => { isMounted = false; };
    }, [data.region, data.province]);

    useEffect(() => {
        let isMounted = true;
        if (!data.city) {
            setBarangayOptions([]);
            return undefined;
        }
        getPhilippinesBarangays(data.region, data.province, data.city).then((barangays) => {
            if (isMounted) setBarangayOptions(barangays);
        }).catch((error) => {
            console.error('Failed to load Philippine barangays:', error);
        });
        return () => { isMounted = false; };
    }, [data.region, data.province, data.city]);

    const normalizedData = normalizePhilippinesAddress(data);

    const selectedRegionLabel = useMemo(() => {
        return regionOptions.find((region) => region.label === normalizedData.region || region.name === normalizedData.region)?.label || normalizedData.region || '';
    }, [regionOptions, normalizedData.region]);

    const selectedProvinceLabel = useMemo(() => {
        return provinceOptions.find((province) => province.label === normalizedData.province || province.name === normalizedData.province)?.label || normalizedData.province || '';
    }, [provinceOptions, normalizedData.province]);

    const selectedCityLabel = useMemo(() => {
        return cityOptions.find((city) => city.label === normalizedData.city || city.name === normalizedData.city)?.label || normalizedData.city || '';
    }, [cityOptions, normalizedData.city]);

    const selectedBarangayLabel = useMemo(() => {
        return barangayOptions.find((barangay) => barangay.label === normalizedData.barangay || barangay.name === normalizedData.barangay)?.label || normalizedData.barangay || '';
    }, [barangayOptions, normalizedData.barangay]);

    return (
        <div className="address-card">

            {/* Edit/Save/Cancel Buttons - Top Right */}
            <div className="card-actions">
                {!isEditing ? (
                    <button 
                        className="edit-icon-button" 
                        onClick={() => onEdit(address)}
                        aria-label="Edit address"
                        disabled={saving}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                    </button>
                ) : (
                    <div className="edit-actions">
                        <button 
                            className="cancel-btn" 
                            onClick={() => onCancel(address.id)}
                            disabled={saving}
                        >
                            Cancel
                        </button>
                        <button 
                            className="save-btn" 
                            onClick={() => onSave(address.id)}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                )}
            </div>

            {/* Address Form */}
            <div className="address-form">
                <h3 className="address-card__title">Saved Address</h3>

                {/* Name Row */}
                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label">First Name</label>
                        <input
                            type="text"
                            className={`form-input ${!isEditing ? 'readonly' : ''}`}
                            value={data.firstName || ''}
                            onChange={(e) => onInputChange('firstName', e.target.value)}
                            placeholder="first name"
                            readOnly={!isEditing}
                        />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Last Name</label>
                        <input
                            type="text"
                            className={`form-input ${!isEditing ? 'readonly' : ''}`}
                            value={data.lastName || ''}
                            onChange={(e) => onInputChange('lastName', e.target.value)}
                            placeholder="last name"
                            readOnly={!isEditing}
                        />
                    </div>
                </div>

                {/* House No./Street */}
                <div className="form-group">
                    <label className="form-label">House No./Street</label>
                    <input
                        type="text"
                        className={`form-input ${!isEditing ? 'readonly' : ''}`}
                        value={data.houseNo || ''}
                        onChange={(e) => onInputChange('houseNo', e.target.value)}
                        placeholder="123 Bonifacio Street"
                        readOnly={!isEditing}
                    />
                </div>

                {/* Province & City Row */}
                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label">Region</label>
                        <select
                            className={`form-input ${!isEditing ? 'readonly' : ''}`}
                            value={selectedRegionLabel}
                            onChange={(e) => {
                                const nextAddress = normalizePhilippinesAddress({
                                    ...normalizedData,
                                    region: e.target.value,
                                });
                                onInputChange('region', nextAddress.region);
                                onInputChange('province', '');
                                onInputChange('city', '');
                                onInputChange('barangay', '');
                                onInputChange('zipCode', '');
                            }}
                            disabled={!isEditing}
                        >
                            <option value="">Select a region</option>
                            {regionOptions.map((region) => (
                                <option key={region.code} value={region.label}>{region.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Province</label>
                        <select
                            className={`form-input ${!isEditing ? 'readonly' : ''}`}
                            value={selectedProvinceLabel}
                            onChange={(e) => {
                                onInputChange('province', e.target.value);
                                onInputChange('city', '');
                                onInputChange('barangay', '');
                                onInputChange('zipCode', '');
                            }}
                            disabled={!isEditing || !normalizedData.region}
                        >
                            <option value="">{normalizedData.region ? 'Select a province' : 'Select a region first'}</option>
                            {provinceOptions.map((province) => (
                                <option key={province.code} value={province.label}>{province.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
                {/* City, Barangay & Zip Code Row */}
                <div className="form-row form-row--address-details">
                    <div className="form-group form-group--city">
                        <label className="form-label">City/Municipality</label>
                        <select
                            className={`form-input ${!isEditing ? 'readonly' : ''}`}
                            value={selectedCityLabel}
                            onChange={(e) => {
                                onInputChange('city', e.target.value);
                                onInputChange('barangay', '');
                                onInputChange('zipCode', '');
                            }}
                            disabled={!isEditing || !normalizedData.province}
                        >
                            <option value="">{normalizedData.province ? 'Select a city' : 'Select a province first'}</option>
                            {cityOptions.map((city) => (
                                <option key={city.code} value={city.label}>{city.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group form-group--barangay">
                        <label className="form-label">Barangay</label>
                        <select
                            className={`form-input ${!isEditing ? 'readonly' : ''}`}
                            value={selectedBarangayLabel}
                            onChange={(e) => onInputChange('barangay', e.target.value)}
                            disabled={!isEditing || !data.city}
                        >
                            <option value="">{data.city ? 'Select a barangay' : 'Select a city first'}</option>
                            {barangayOptions.map((barangay) => (
                                <option key={barangay.code} value={barangay.label}>{barangay.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group form-group--zip">
                        <label className="form-label">Zip Code</label>
                        <input
                            type="text"
                            className={`form-input ${!isEditing ? 'readonly' : ''}`}
                            value={data.zipCode || ''}
                            onChange={(e) => onInputChange('zipCode', e.target.value)}
                            placeholder="0000"
                            maxLength="4"
                            readOnly={!isEditing}
                        />
                    </div>
                </div>

                {/* Landmark */}
                <div className="form-group">
                    <label className="form-label">Landmark</label>
                    <input
                        type="text"
                        className={`form-input ${!isEditing ? 'readonly' : ''}`}
                        value={data.landmark || ''}
                        onChange={(e) => onInputChange('landmark', e.target.value)}
                        placeholder="Near Landmark"
                        readOnly={!isEditing}
                    />
                </div>

                {/* Default Address — shown when NOT editing */}
                {!isEditing && (
                    <div className="default-address-section">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={address.isDefault || false}
                                onChange={() => onSetDefault(address.id)}
                                disabled={saving}
                            />
                            <span>Set as default address</span>
                        </label>
                        {address.isDefault && (
                            <span className="default-badge">Default</span>
                        )}
                    </div>
                )}

                {/* Delete button — shown when NOT editing and there are 2+ addresses */}
                {!isEditing && showDelete && (
                    <button
                        className="delete-button"
                        onClick={() => onDelete(address.id)}
                        disabled={saving}
                        aria-label="Delete address"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                            <line x1="10" y1="11" x2="10" y2="17"/>
                            <line x1="14" y1="11" x2="14" y2="17"/>
                        </svg>
                        Delete Address
                    </button>
                )}

            </div>
        </div>
    );
}

export default AddressCard;